// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import * as Sentry from "@sentry/react";
import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { mdiUndo } from "@mdi/js";
import { Button } from "antd";
import {
  MY_FOCUS_FIELDS,
  PROJECT_FOCUS_FIELDS,
  SHAPEFILE_FIELDS
} from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { getFavouriteColumns } from "api/column";
import {
  deleteOrganizationFocusTilesDefault,
  getOrganizationFocusTilesDefault,
  setOrganizationFocusTilesDefault
} from "api/users";

import { EntityKind } from "models/entityKind";
import { OrganizationFocusTilesDefault } from "models/organizationFocusTilesDefault";

import FocusTilePicker from "components/user-settings/components/defaults/FocusTilePicker";
import { StyledSpinner } from "components/user-settings/components/defaults/shared";
import { useUserContext } from "components/user/context";
import { updateSettingFocus } from "components/user/context/reducer";
import { updateSettingFacilityFocus } from "components/user/context/reducer/UserReducer";
import { useUserDefaults } from "components/user/hooks";
import { resolveFocusTiles } from "components/user/hooks/resolveFocusTiles";
import { toFocusTileProperties } from "components/user/hooks/toFocusTileProperties";
import { useUserSettingsQueryKeys } from "components/user/queries/useUserSettingsDefaults/useUserSettingsDefaultsQuery";

// An org default reaches every user in the org, so per-user and per-project fields are out.
const ORG_EXCLUDED_CATEGORIES = [SHAPEFILE_FIELDS, PROJECT_FOCUS_FIELDS, MY_FOCUS_FIELDS];

const ORG_DEFAULT_FOCUS_TILES_QUERY_KEY = "OrgDefaultFocusTiles";
const FOCUS_TILES_SEED_QUERY_KEY = "OrgDefaultFocusTilesSeed";

// Validation failures are plain strings; model-binding failures are ProblemDetails objects.
const errorDetail = (error: { response?: { data?: unknown } }) =>
  typeof error.response?.data === "string" ? ` ${error.response.data}` : "";

interface OrganizationFocusTileDefaultsProps {
  entityKind: EntityKind;
}

const OrganizationFocusTileDefaults = ({
  entityKind
}: OrganizationFocusTileDefaultsProps) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const queryClient = useQueryClient();
  const [, dispatch] = useUserContext();
  const defaults = useUserDefaults();
  const organizationId = user?.organization?.id;
  const kindLabel = entityKind === EntityKind.Well ? "well" : "facility";
  const queryKey = [ORG_DEFAULT_FOCUS_TILES_QUERY_KEY, organizationId, entityKind];

  const {
    data: orgDefault,
    isError,
    isSuccess
  } = useQuery(
    queryKey,
    () => getOrganizationFocusTilesDefault(organizationId, entityKind),
    { enabled: !!organizationId }
  );
  const hasSavedDefault = orgDefault != null;

  const { data: seedTiles, isError: isSeedError } = useQuery(
    [FOCUS_TILES_SEED_QUERY_KEY, organizationId, entityKind],
    async () => toFocusTileProperties((await getFavouriteColumns(entityKind)).data),
    { enabled: !!organizationId && isSuccess && !hasSavedDefault }
  );

  // Bumped when a save or Reset starts, so a picker callback queued from an earlier render
  // (e.g. the debounced Add) cannot write over it with a stale list, and an earlier
  // change's context refresh cannot land after a later one's.
  const generationRef = useRef(0);
  const generation = generationRef.current;

  const refreshAfterChange = async (saved: OrganizationFocusTilesDefault | null) => {
    const changeGeneration = generationRef.current;
    // Until the refetch lands the picker would still show the old list to edit from.
    queryClient.setQueryData(queryKey, saved);
    queryClient.invalidateQueries(queryKey);
    queryClient.invalidateQueries(useUserSettingsQueryKeys.userSettings);

    // Without a personal list the admin resolves to the org default, and the loader would
    // keep the stale in-context copy rather than the new one.
    const personal =
      entityKind === EntityKind.Well
        ? defaults?.focusTiles
        : defaults?.facilityFocusTiles;
    if (personal?.id) return;

    try {
      const resolved = await resolveFocusTiles(entityKind);
      if (changeGeneration !== generationRef.current) return;
      if (entityKind === EntityKind.Well) updateSettingFocus(dispatch, resolved);
      else updateSettingFacilityFocus(dispatch, resolved);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "org-settings.refresh-focus-tiles" },
        extra: { organizationId, entityKind }
      });
    }
  };

  const saveMutation = useMutation(
    (tiles: string[]) =>
      setOrganizationFocusTilesDefault(organizationId, entityKind, tiles),
    {
      onSuccess: (saved) => {
        toast.success(
          `Successfully saved organization default ${kindLabel} focus tiles.`
        );
        refreshAfterChange(saved);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        Sentry.captureException(error, {
          tags: { feature: "org-settings.save-focus-tiles-default" },
          extra: { organizationId, entityKind }
        });
        toast.error(
          error.response
            ? `An error occurred.${errorDetail(error)}`
            : "Unable to connect to network."
        );
      }
    }
  );

  const resetMutation = useMutation(
    () => deleteOrganizationFocusTilesDefault(organizationId, entityKind),
    {
      onSuccess: () => {
        toast.success(
          `Reset the organization default ${kindLabel} focus tiles — users now fall back to the system default.`
        );
        refreshAfterChange(null);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        Sentry.captureException(error, {
          tags: { feature: "org-settings.clear-focus-tiles-default" },
          extra: { organizationId, entityKind }
        });
        toast.error(
          error.response
            ? `An error occurred.${errorDetail(error)}`
            : "Unable to connect to network."
        );
      }
    }
  );

  const tiles = orgDefault?.tiles ?? seedTiles ?? [];
  const isBusy = saveMutation.isLoading || resetMutation.isLoading;

  const handleChange = (next: string[]) => {
    if (isBusy || generation !== generationRef.current) return;
    generationRef.current++;
    saveMutation.mutate(next);
  };

  const handleReset = () => {
    generationRef.current++;
    resetMutation.mutate();
  };

  // Rendering the picker without a successful GET would let an edit PUT over the real default.
  if (isError || isSeedError) {
    return (
      <StatusLine data-testid={`org-focus-tiles-default-error-${entityKind}`}>
        Unable to load the {isError ? "organization" : "system"} default {kindLabel} focus
        tiles.
      </StatusLine>
    );
  }

  // Until the seed arrives an Add would PUT a one-tile list in place of the favourites shown.
  if (!isSuccess || (!hasSavedDefault && seedTiles === undefined)) {
    return (
      <StyledSpinner data-testid={`org-focus-tiles-default-loading-${entityKind}`} />
    );
  }

  return (
    <FocusTilePicker
      entityKind={entityKind}
      title={`Organization Default ${entityKind} Focus Tiles`}
      tiles={tiles}
      onChange={handleChange}
      isSaving={isBusy}
      excludedCategoryFilters={ORG_EXCLUDED_CATEGORIES}
      capRawTiles
      headerActions={
        hasSavedDefault && (
          <Button
            type="text"
            data-testid={`reset-org-focus-tiles-default-${entityKind}`}
            disabled={saveMutation.isLoading}
            loading={resetMutation.isLoading}
            onClick={handleReset}
            icon={<Icon path={mdiUndo} size={1} />}>
            Reset
          </Button>
        )
      }
    />
  );
};

export default OrganizationFocusTileDefaults;

const StatusLine = styled.div`
  color: var(--color-text-secondary, inherit);
  font-size: 1.2rem;
`;
